import React, { useState, useEffect } from "react";
import { PlxsButton, PlxsAlert } from "@plexlogic/plexus-ui";
import { DrawData } from "../../types/DrawData";
import { KeyValueTable, WizardStepHeader } from "../index";
import WizardFooterNav from "../WizardFooterNav";
import { getRowCount } from "../uploadEntries/useUploadStore";
import { toSentenceCase } from "../../utils/stringUtils";
import ScrutineerDetails from "../drawDetails/ScrutineerDetails";

interface ReviewStepProps {
  drawData: DrawData;
  drawId?: string;
  onNext: () => void;
  onDrawCreated?: (drawId: string, drawNo?: string) => void;
  onBack: () => void;
  updateDrawData?: (updates: Partial<DrawData>) => void;
  isLocked?: boolean;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  drawData,
  drawId,
  onNext,
  onBack,
  updateDrawData,
  isLocked = false,
}) => {
  // State for scrutineer details
  const [scrutineerName, setScrutineerName] = useState(
    drawData.scrutineerSettings?.scrutineerName || ""
  );
  const [scrutineerEmail, setScrutineerEmail] = useState(
    drawData.scrutineerSettings?.scrutineerEmail || ""
  );
  const [scrutineerJobTitle, setScrutineerJobTitle] = useState(
    drawData.scrutineerSettings?.scrutineerJobTitle || ""
  );
  const [scrutineerConfirmation, setScrutineerConfirmation] = useState(
    drawData.scrutineerSettings?.scrutineerConfirmation || false
  );
  const computeShowReview = (): boolean => {
    const s = drawData.scrutineerSettings;
    if (!s?.scrutineerRequired) return true;
    if (isLocked) return false;
    return (
      !!s.scrutineerName &&
      !!s.scrutineerEmail &&
      !!s.scrutineerJobTitle &&
      !!s.scrutineerConfirmation
    );
  };
  const [showReview, setShowReview] = useState(computeShowReview());

  // Sync local state with drawData when scrutineer settings change
  // This handles the case when user goes back and modifies prize pool or scrutineer requirement
  useEffect(() => {
    setScrutineerName(drawData.scrutineerSettings?.scrutineerName || "");
    setScrutineerEmail(drawData.scrutineerSettings?.scrutineerEmail || "");
    setScrutineerJobTitle(
      drawData.scrutineerSettings?.scrutineerJobTitle || ""
    );
    setScrutineerConfirmation(
      drawData.scrutineerSettings?.scrutineerConfirmation || false
    );

    // Reset showReview state when scrutineer requirement changes
    setShowReview(computeShowReview());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawData.scrutineerSettings, isLocked]);

  const totalWinners = drawData.prizes.reduce(
    (sum, prize) => sum + prize.winners,
    0
  );
  const totalReserves = drawData.prizes.reduce(
    (sum, prize) => sum + prize.reserves,
    0
  );

  const runsOnConfirm = !drawData.scheduledDateTime;

  /**
   * Check if scheduled datetime is expired or expiring soon
   * @returns 'expired' if in the past, 'expiring' if within 5 minutes, null otherwise
   */
  const checkScheduledDateTimeStatus = (): "expired" | "expiring" | null => {
    if (!drawData.scheduledDateTime) return null;

    const now = new Date();
    const scheduledTime = new Date(drawData.scheduledDateTime);
    const minAllowedTime = new Date(now.getTime() - 5000); // 5 seconds buffer
    const expiringThreshold = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes from now

    if (scheduledTime < minAllowedTime) {
      return "expired";
    } else if (scheduledTime <= expiringThreshold) {
      return "expiring";
    }
    return null;
  };

  /**
   * Handle scrutineer form submission
   * Save scrutineer details and show review content
   */
  const handleScrutineerNext = () => {
    if (isLocked) {
      setShowReview(true);
      return;
    }

    // Validate scrutineer fields
    if (
      scrutineerName.trim() === "" ||
      scrutineerEmail.trim() === "" ||
      scrutineerJobTitle.trim() === "" ||
      !scrutineerConfirmation
    ) {
      return; // Validation will be handled by ScrutineerDetails component
    }

    // Save scrutineer details to drawData
    if (updateDrawData) {
      updateDrawData({
        scrutineerSettings: {
          ...drawData.scrutineerSettings,
          scrutineerRequired: true,
          scrutineerName,
          scrutineerEmail,
          scrutineerJobTitle,
          scrutineerConfirmation,
        },
      });
    }

    // Show review content
    setShowReview(true);
  };

  /**
   * Handle edit scrutineer details
   * Return to scrutineer form to allow editing
   */
  const handleEditScrutineerDetails = () => {
    setShowReview(false);
  };

  /**
   * Handle next button click
   * Always proceed to Payment step (for both scheduled and immediate draws)
   */
  const handleNext = () => {
    // Always proceed to next step (Payment step)
    // Both scheduled and immediate draws need to go through payment
    onNext();
  };

  // If scrutineer is required and form not yet filled, show scrutineer form
  if (drawData.scrutineerSettings?.scrutineerRequired === true && !showReview) {
    return (
      <div
        style={{
          animation: "fadeIn 0.3s ease-in",
        }}
      >
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
        <ScrutineerDetails
          scrutineerName={
            isLocked
              ? drawData.scrutineerSettings?.scrutineerName || scrutineerName
              : scrutineerName
          }
          scrutineerEmail={
            isLocked
              ? drawData.scrutineerSettings?.scrutineerEmail || scrutineerEmail
              : scrutineerEmail
          }
          scrutineerJobTitle={
            isLocked
              ? drawData.scrutineerSettings?.scrutineerJobTitle ||
                scrutineerJobTitle
              : scrutineerJobTitle
          }
          scrutineerConfirmation={
            isLocked
              ? drawData.scrutineerSettings?.scrutineerConfirmation ??
                scrutineerConfirmation
              : scrutineerConfirmation
          }
          onScrutineerNameChange={setScrutineerName}
          onScrutineerEmailChange={setScrutineerEmail}
          onScrutineerJobTitleChange={setScrutineerJobTitle}
          onScrutineerConfirmationChange={setScrutineerConfirmation}
          onNext={handleScrutineerNext}
          onBack={onBack}
          isLocked={isLocked}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        animation: "fadeIn 0.3s ease-in",
      }}
    >
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}
      </style>
      <WizardStepHeader
        title={toSentenceCase("one last look")}
        description={toSentenceCase(
          "check everything's right – you can go back and change any step."
        )}
      />
      {/* Consolidated Draw Details */}
      <KeyValueTable
        title={toSentenceCase("draw details")}
        items={[
          // Basic Draw Information
          {
            key: toSentenceCase("draw name"),
            value: drawData.drawName,
          },
          {
            key: toSentenceCase("run by"),
            value: drawData.organisation,
            hidden: !drawData.organisation,
          },

          // Entry Configuration
          {
            key: toSentenceCase("entry method"),
            value:
              drawData.entryMethod === "simple"
                ? toSentenceCase("entry count only")
                : drawData.entryMethod === "standard"
                ? toSentenceCase("standard file upload")
                : toSentenceCase("advanced file upload"),
          },
          {
            key: toSentenceCase("number of entries"),
            value: drawData.entryCount,
            hidden: drawData.entryMethod !== "simple",
          },
          {
            key: toSentenceCase("uploaded file"),
            value:
              drawData.uploadedEntries?.originalFileName ||
              drawData.uploadedEntries?.fileName ||
              "Unknown file",
            hidden:
              !(
                drawData.entryMethod === "standard" ||
                drawData.entryMethod === "advanced"
              ) || !drawData.uploadedEntries,
          },
          {
            key: toSentenceCase("file details"),
            value: drawData.uploadedEntries?.rowCount ? (
              <>
                {getRowCount(drawData.uploadedEntries).toLocaleString()} rows
                {drawData.csvConfirmationSettings?.isHeaderRow
                  ? " (with header)"
                  : ""}
              </>
            ) : (
              ""
            ),
            hidden: !drawData.uploadedEntries?.rowCount,
          },
          {
            key: toSentenceCase("entries column"),
            value: drawData.csvConfirmationSettings?.ticketCountColumn,
            hidden:
              drawData.entryMethod !== "advanced" ||
              !drawData.csvConfirmationSettings?.ticketCountColumn,
          },

          // Prize Summary
          {
            key: toSentenceCase("total winners"),
            value: totalWinners,
          },
          {
            key: toSentenceCase("total reserves"),
            value: totalReserves,
          },
          {
            key: toSentenceCase("number of prizes"),
            value: drawData.prizes.length,
          },
        ]}
      />
      {/* Prize Details */}
      <KeyValueTable
        title={toSentenceCase("prizes")}
        items={drawData.prizes.map((prize, index) => ({
          key: toSentenceCase(`prize ${index + 1}`),
          value: (
            <div style={{ display: "flex", gap: "var(--plexus-space-lg)" }}>
              <div>
                <span style={{ color: "var(--plexus-color-neutral-200)" }}>
                  {prize.description || toSentenceCase("no description")}
                </span>
              </div>
              <div>
                <span
                  style={{
                    color: "var(--plexus-color-neutral-300)",
                    fontSize: "0.875rem",
                  }}
                >
                  {toSentenceCase("winners")}:{" "}
                </span>
                <span style={{ fontWeight: 600 }}>{prize.winners}</span>
              </div>
              <div>
                <span
                  style={{
                    color: "var(--plexus-color-neutral-300)",
                    fontSize: "0.875rem",
                  }}
                >
                  {toSentenceCase("reserves")}:{" "}
                </span>
                <span style={{ fontWeight: 600 }}>{prize.reserves}</span>
              </div>
            </div>
          ),
        }))}
      />
      {/* Scrutineer Details (if required) */}
      {drawData.scrutineerSettings?.scrutineerRequired === true && (
        <div
          style={{
            position: "relative",
            marginBottom: "var(--plexus-space-lg)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "var(--plexus-space-sm)",
            }}
          >
            <div
              style={{
                fontSize: "1rem",
                fontWeight: 600,
                color: "var(--plexus-color-neutral-100)",
              }}
            >
              {toSentenceCase("scrutineer details")}
            </div>
            <span className="wizard-locked-action">
              <PlxsButton
                label={toSentenceCase(isLocked ? "view" : "edit")}
                variant="secondary"
                tone="brand"
                size="sm"
                onClick={handleEditScrutineerDetails}
                dataTestId="edit-scrutineer-details-button"
              />
            </span>
          </div>
          <KeyValueTable
            items={[
              {
                key: toSentenceCase("scrutineer required"),
                value: toSentenceCase("yes"),
              },
              {
                key: toSentenceCase("scrutineer name"),
                value:
                  scrutineerName ||
                  drawData.scrutineerSettings?.scrutineerName ||
                  toSentenceCase("not provided"),
              },
              {
                key: toSentenceCase("scrutineer email"),
                value:
                  scrutineerEmail ||
                  drawData.scrutineerSettings?.scrutineerEmail ||
                  toSentenceCase("not provided"),
              },
              {
                key: toSentenceCase("scrutineer job title"),
                value:
                  scrutineerJobTitle ||
                  drawData.scrutineerSettings?.scrutineerJobTitle ||
                  toSentenceCase("not provided"),
              },
            ]}
          />
        </div>
      )}
      {/* Draw Execution Time */}
      <KeyValueTable
        title={toSentenceCase("draw execution time")}
        items={[
          {
            key: toSentenceCase("execution type"),
            value: (() => {
              const scrutineerRequired =
                drawData.scrutineerSettings?.scrutineerRequired === true;

              if (scrutineerRequired) {
                return toSentenceCase("immediate (scrutineer required)");
              } else if (drawData.scheduledDateTime) {
                return toSentenceCase("scheduled draw");
              } else {
                return toSentenceCase("immediate draw");
              }
            })(),
          },
          {
            key: toSentenceCase("scheduled for"),
            value: drawData.scheduledDateTime
              ? new Date(drawData.scheduledDateTime).toLocaleString("en-US", {
                  dateStyle: "medium",
                  timeStyle: "short",
                })
              : undefined,
            hidden:
              !drawData.scheduledDateTime ||
              drawData.scrutineerSettings?.scrutineerRequired === true,
          },
        ]}
      />

      {/* Warning for expired or expiring scheduled datetime */}
      {drawData.scheduledDateTime &&
        (() => {
          const status = checkScheduledDateTimeStatus();
          if (status === "expired") {
            return (
              <div style={{ marginBottom: "var(--plexus-space-lg)" }}>
                <PlxsAlert
                  variant="warning"
                  dataTestId="scheduled-datetime-expired-alert"
                >
                  The scheduled date and time has passed. Please go back to the
                  scheduling step and select a future date and time.
                </PlxsAlert>
              </div>
            );
          } else if (status === "expiring") {
            return (
              <div style={{ marginBottom: "var(--plexus-space-lg)" }}>
                <PlxsAlert
                  variant="warning"
                  dataTestId="scheduled-datetime-expiring-alert"
                >
                  The scheduled date and time is approaching soon (within 5
                  minutes). Consider updating it in the scheduling step if you
                  need more time.
                </PlxsAlert>
              </div>
            );
          }
          return null;
        })()}

      {/* Warnings */}
      {/* Note: Validation is enforced in PrizeConfigurationStep, so these warnings should never appear */}

      <WizardFooterNav
        backButton={{
          dataTestId: "back-review",
          label: toSentenceCase("back"),
          variant: "secondary",
          tone: "neutral",
          onClick: onBack,
        }}
        continueButton={{
          dataTestId: "next-review",
          label: runsOnConfirm ? "Run my draw" : "Schedule my draw",
          variant: "primary",
          onClick: handleNext,
        }}
      />
    </div>
  );
};

export default ReviewStep;
